import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, IconButton, LinearProgress, ToggleButton, ToggleButtonGroup, Tooltip, Typography, useTheme } from '@mui/material';
import React, { FC, ReactNode, useMemo } from 'react';
import type { YearsRevenueMode } from '../Reports/indianFinancialYear';
import { STATUS_COLORS } from '../Reports/echartsConfig';
import MiniTrendChart, { MiniTrendPoint } from './MiniTrendChart';
import { formatCurrencyWithSymbol } from 'utils/hooks/getCurrency';

export type { MiniTrendPoint };

export type RevenueTheme = 'daily' | 'mtd' | 'ytd';

export interface RevenueMetric {
  title: string;
  /** Shown beside the title in smaller, sentence case (e.g. today’s date or a range in parentheses). */
  titleContext?: string;
  value: number;
  percentageChange: number;
  compareText: string;
  trend: MiniTrendPoint[];
  /** Confirmed line items in the same window as `value` (from API). */
  bookingCount?: number;
  averageRevenuePerBooking?: number;
  /** Plain text from API; newlines preserved in tooltip. */
  tooltip?: string;
  /**
   * Manual budget total for this card’s period (non-calculated budgets, start date in period). Omitted for daily.
   */
  budget?: number;
  /** Actualized revenue in the same window as `value` (from API). */
  actualizedRevenue?: number;
}

interface RevenueCardProps {
  metric: RevenueMetric;
  themeKey: RevenueTheme;
  icon: ReactNode;
  /** Years Revenue only: FY vs calendar year. */
  yearMode?: YearsRevenueMode;
  onYearModeChange?: (mode: YearsRevenueMode) => void;
}

const formatINR = (value: number) =>
  formatCurrencyWithSymbol(value);

/** Mini chart height in the top-right of the KPI card. */
const KPI_TREND_CHART_PX = 40;
/** Reserve space when month/year cards have a budget row but daily does not (keeps card heights even). */
const KPI_BUDGET_ROW_PLACEHOLDER_PX = 24;
const DEFAULT_COMPARE_TEXT: Record<RevenueTheme, string> = {
  daily: 'vs Day Before',
  mtd: 'vs Last Month',
  ytd: 'vs Prior FY',
};

const TOOLTIP_SLOT_PROPS = {
  tooltip: {
    sx: {
      bgcolor: 'rgba(17, 24, 39, 0.94)',
      maxWidth: 320,
      '& .MuiTooltip-arrow': { color: 'rgba(17, 24, 39, 0.94)' },
    },
  },
} as const;

const RevenueBreakdownLegendDot: FC<{ color: string }> = ({ color }) => (
  <Box
    component="span"
    aria-hidden
    sx={{
      width: 8,
      height: 8,
      borderRadius: '50%',
      bgcolor: color,
      flexShrink: 0,
    }}
  />
);

const RevenueBreakdownTooltip: FC<{ metric: RevenueMetric }> = ({ metric }) => {
  const total = Number(metric.value) || 0;
  const actualizedRaw = Number(metric.actualizedRevenue);
  const hasActualized = Number.isFinite(actualizedRaw);
  const actualized = hasActualized ? Math.max(0, actualizedRaw) : 0;
  /** Confirmed bookings not yet actualized; with actualized forms total revenue. */
  const confirmedOnly = hasActualized ? Math.max(0, total - actualized) : total;
  const budgetRaw = metric.budget;

  const rows: { label: string; value: string; color?: string }[] = [
    {
      label: 'Confirmed',
      value: formatINR(confirmedOnly),
      color: STATUS_COLORS.confirmed,
    },
    {
      label: 'Actualized',
      value: hasActualized ? formatINR(actualized) : '—',
      color: STATUS_COLORS.actualized,
    },
    { label: 'Total revenue', value: formatINR(total) },
    {
      label: 'Budget',
      value: budgetRaw != null && Number.isFinite(Number(budgetRaw)) ? formatINR(Number(budgetRaw)) : '—',
      color: STATUS_COLORS.budget,
    },
  ];

  return (
    <Box component="div" sx={{ py: 0.25, minWidth: 220 }}>
      {rows.map((row, index) => (
        <Box
          key={row.label}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            py: 0.35,
            ...(index === 1
              ? {
                  pb: 0.6,
                  mb: 0.35,
                  borderBottom: '1px solid',
                  borderColor: 'rgba(148, 163, 184, 0.2)',
                }
              : {}),
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flex: 1, minWidth: 0 }}>
            {row.color ? (
              <RevenueBreakdownLegendDot color={row.color} />
            ) : (
              <Box component="span" sx={{ width: 8, flexShrink: 0 }} aria-hidden />
            )}
            <Typography
              component="span"
              variant="body2"
              sx={{
                fontWeight: row.color ? 600 : 700,
                color: row.color ? 'grey.300' : 'grey.100',
              }}>
              {row.label}
            </Typography>
          </Box>
          <Typography
            component="span"
            variant="body2"
            sx={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: 'common.white' }}>
            {row.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const RevenueCard: FC<RevenueCardProps> = ({ metric, themeKey, icon, yearMode, onYearModeChange }) => {
  const theme = useTheme();
  const showYearModeToggle = themeKey === 'ytd' && Boolean(onYearModeChange);

  const colors = useMemo(() => {
    switch (themeKey) {
      case 'daily':
        return { main: '#16a34a', softBg: '#dcfce7' };
      case 'mtd':
        return { main: '#2563eb', softBg: '#dbeafe' };
      case 'ytd':
      default:
        return { main: '#7c3aed', softBg: '#ede9fe' };
    }
  }, [themeKey]);

  const hasTrend = Array.isArray(metric.trend) && metric.trend.length > 0;

  const budgetAmount = metric.budget;
  const hasBudgetTarget = budgetAmount != null && budgetAmount > 0;
  const pctOfBudget = hasBudgetTarget ? (metric.value / budgetAmount) * 100 : 0;
  const progressBarValue = hasBudgetTarget ? Math.min(pctOfBudget, 100) : 0;
  const overBudget = hasBudgetTarget && metric.value > budgetAmount;
  const delta = metric.percentageChange ?? 0;
  const isPositive = delta > 0;
  const isNegative = delta < 0;
  const pctAbs = Math.abs(delta);
  const pctText = `${isPositive ? '+' : isNegative ? '-' : ''}${pctAbs.toFixed(1)}%`;

  const changeChip = isPositive
    ? { bg: '#dcfce7', fg: '#166534', Icon: ArrowUpwardRoundedIcon }
    : isNegative
    ? { bg: '#fee2e2', fg: '#991b1b', Icon: ArrowDownwardRoundedIcon }
    : { bg: '#f3f4f6', fg: '#4b5563', Icon: null };

  const ChangeIcon = changeChip.Icon;
  const compareLabel = String(metric.compareText ?? '').trim() || DEFAULT_COMPARE_TEXT[themeKey];

  return (
    <Tooltip
      arrow
      describeChild
      enterTouchDelay={0}
      placement="top"
      title={<RevenueBreakdownTooltip metric={metric} />}
      slotProps={TOOLTIP_SLOT_PROPS}>
      <Box
        sx={{
          backgroundColor: '#FFFFFF',
          border: '1px solid #e5e7eb',
          borderRadius: '14px',
          boxShadow: '0 6px 18px rgba(16, 24, 40, 0.08)',
          p: 2.25,
          display: 'flex',
          gap: 2,
          alignItems: 'stretch',
          minHeight: 132,
          width: '100%',
          height: '100%',
          boxSizing: 'border-box',
          cursor: 'default',
        }}>
      <Box
        sx={{
          flex: '0 0 auto',
          width: 44,
          height: 44,
          borderRadius: '999px',
          backgroundColor: colors.softBg,
          color: colors.main,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        {icon}
      </Box>

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.5, minWidth: 0 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                flexWrap: 'wrap',
                minWidth: 0,
              }}>
              <Typography
                component="span"
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  textTransform: 'uppercase',
                  color: theme.palette.text.secondary,
                  lineHeight: 1.2,
                }}>
                {metric.title}
              </Typography>
              {showYearModeToggle ? (
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={yearMode ?? 'FISCAL'}
                  onChange={(_e, next: YearsRevenueMode | null) => {
                    if (next && onYearModeChange) onYearModeChange(next);
                  }}
                  onClick={e => e.stopPropagation()}
                  onMouseDown={e => e.stopPropagation()}
                  aria-label="Years revenue period"
                  sx={{
                    flexShrink: 0,
                    '& .MuiToggleButton-root': {
                      px: 0.9,
                      py: 0.15,
                      fontSize: 10,
                      fontWeight: 700,
                      lineHeight: 1.4,
                      textTransform: 'none',
                    },
                  }}>
                  <ToggleButton value="FISCAL">Apr–Mar</ToggleButton>
                  <ToggleButton value="CALENDAR">Jan–Dec</ToggleButton>
                </ToggleButtonGroup>
              ) : null}
            </Box>
            {metric.titleContext ? (
              <Typography
                component="span"
                sx={{
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: 0.02,
                  textTransform: 'none',
                  color: theme.palette.text.secondary,
                  opacity: 0.92,
                  lineHeight: 1.2,
                }}>
                {metric.titleContext}
              </Typography>
            ) : null}
          </Box>
          {metric.tooltip ? (
            <Tooltip
              arrow
              describeChild
              enterTouchDelay={0}
              title={
                <Typography
                  component="div"
                  variant="body2"
                  sx={{ maxWidth: 380, whiteSpace: 'pre-line', lineHeight: 1.55 }}>
                  {metric.tooltip}
                </Typography>
              }
              slotProps={TOOLTIP_SLOT_PROPS}>
              <IconButton
                size="small"
                aria-label="How this metric is calculated"
                sx={{
                  p: 0.25,
                  flexShrink: 0,
                  color: 'text.disabled',
                  '&:hover': { color: 'text.secondary', bgcolor: 'action.hover' },
                }}>
                <InfoOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          ) : null}
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: hasTrend ? (themeKey === 'ytd' ? 3 : 2.5) : 1.5,
          }}>
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              maxWidth: hasTrend ? (themeKey === 'ytd' ? 'calc(100% - 132px)' : 'calc(100% - 120px)') : '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              pr: hasTrend ? (themeKey === 'ytd' ? 1.3 : 0.5) : 0,
            }}>
            <Box
              component="div"
              sx={{
                pt: 0.25,
                display: 'flex',
                alignItems: 'baseline',
                flexWrap: 'wrap',
                gap: 0.25,
                columnGap: 0.5,
              }}>
              <Typography
                component="span"
                sx={{ fontSize: 22, fontWeight: 800, color: theme.palette.text.primary, lineHeight: 1.1 }}>
                {formatINR(metric.value)}
              </Typography>
              {hasBudgetTarget ? (
                <>
                  <Typography
                    component="span"
                    sx={{
                      fontSize: 10,
                      fontWeight: 600,
                      color: 'text.secondary',
                      lineHeight: 1.2,
                      opacity: 0.85,
                    }}>
                    /
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: 'text.secondary',
                      lineHeight: 1.2,
                      fontVariantNumeric: 'tabular-nums',
                    }}>
                    {formatINR(budgetAmount)}
                  </Typography>
                </>
              ) : null}
            </Box>
            {hasBudgetTarget ? (
              <Box
                sx={{
                  mt: 0.5,
                  pt: 0.25,
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}>
                <LinearProgress
                  variant="determinate"
                  value={progressBarValue}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    height: 8,
                    borderRadius: 1,
                    bgcolor: theme.palette.divider,
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 1,
                      backgroundColor: overBudget ? theme.palette.warning.main : colors.main,
                    },
                  }}
                />
                <Typography
                  component="span"
                  sx={{
                    flex: '0 0 auto',
                    fontSize: 11,
                    fontWeight: 700,
                    color: overBudget ? theme.palette.warning.main : theme.palette.text.secondary,
                    fontVariantNumeric: 'tabular-nums',
                    lineHeight: 1,
                    minWidth: 28,
                    textAlign: 'right',
                  }}>
                  {`${pctOfBudget.toFixed(0)}%`}
                </Typography>
              </Box>
            ) : hasTrend ? (
              <Box
                aria-hidden
                sx={{
                  mt: 0.5,
                  pt: 0.25,
                  minHeight: KPI_BUDGET_ROW_PLACEHOLDER_PX,
                  pointerEvents: 'none',
                }}
              />
            ) : null}
          </Box>
          {hasTrend ? (
            <Box
              sx={{
                flex: '0 0 auto',
                width: 100,
                height: KPI_TREND_CHART_PX,
                flexShrink: 0,
                ml: themeKey === 'ytd' ? 1.75 : 0.75,
                mr: themeKey === 'ytd' ? -0.25 : 0,
                opacity: 0.92,
                borderRadius: 1,
                overflow: 'hidden',
                alignSelf: 'flex-start',
              }}>
              <MiniTrendChart data={metric.trend} color={colors.main} />
            </Box>
          ) : null}
        </Box>

        {metric.bookingCount != null && metric.averageRevenuePerBooking != null ? (
          <Typography
            sx={{
              fontSize: 12.5,
              color: theme.palette.text.secondary,
              fontWeight: 500,
              mt: hasTrend ? 0.35 : 0.75,
              lineHeight: 1.45,
            }}>
            {metric.bookingCount} {metric.bookingCount === 1 ? 'Booking' : 'Bookings'} · Avg{' '}
            {formatINR(metric.averageRevenuePerBooking)}
          </Typography>
        ) : null}

        {metric.budget != null && !hasBudgetTarget ? (
          <Typography
            sx={{
              fontSize: 12,
              color: theme.palette.text.secondary,
              fontWeight: 650,
              mt: 0.5,
              lineHeight: 1.45,
            }}>
            Budget {formatINR(metric.budget)}
          </Typography>
        ) : null}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mt: 'auto',
            pt: hasTrend ? 0.75 : 1,
            flexWrap: 'wrap',
          }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.35,
              px: 1,
              py: 0.35,
              borderRadius: 999,
              backgroundColor: changeChip.bg,
              color: changeChip.fg,
              fontSize: 12,
              fontWeight: 700,
              lineHeight: 1.2,
            }}>
            {ChangeIcon ? <ChangeIcon sx={{ fontSize: 16 }} /> : null}
            {pctText}
          </Box>
          <Typography sx={{ fontSize: 12.5, color: theme.palette.text.secondary, fontWeight: 600 }}>
            {compareLabel}
          </Typography>
        </Box>
      </Box>
    </Box>
    </Tooltip>
  );
};

export default RevenueCard;
